import { PathHelper } from '@sensenet/client-utils';
import { v1 } from 'uuid';

/**
 * Response model for Uploads
 */

/**
 * Helper class for uploading content into the sensenet Repository
 */
export class Upload {
  constructor(repository) {
    this.repository = repository;
  }
  /**
   * Uploads a specified text as a binary file
   * @param {UploadTextOptions} options The additional options
   */
  async textAsFile(options) {
    const uploadFileOptions = Object.assign({
      file: new File([options.text], options.fileName)
    }, options);
    return await this.file(uploadFileOptions);
  }

  /**
   * Uploads a specified file into a sensenet Repository
   * @param {UploadFileOptions} options The additional upload options
   */
  async file(options) {
    if (this.isChunkedUploadNeeded(options.file)) {
      return await this.uploadChunked(options);
    }
    return await this.uploadNonChunked(options);
  }

  /**
   * Returns if a chunked upload is needed for a specified file
   * @param {File} file The File object
   */
  isChunkedUploadNeeded(file) {
    return file.size >= this.repository.configuration.chunkSize;
  }
  getUploadUrl(options) {
    return PathHelper.joinPaths(this.repository.configuration.repositoryUrl, this.repository.configuration.oDataToken, PathHelper.getContentUrl(options.parentPath), 'upload');
  }
  getFormDataFromOptions(options) {
    const formData = new FormData();
    formData.append('ChunkToken', '0*0*False*False');
    formData.append('FileName', options.fileName || options.file.name);
    formData.append('Overwrite', options.overwrite.toString());
    formData.append('PropertyName', options.binaryPropertyName.toString());
    formData.append('FileLength', options.file.size.toString());
    if (options.contentTypeName) {
      formData.append('ContentType', options.contentTypeName.toString());
    }
    return formData;
  }
  async uploadNonChunked(options) {
    var _options$progressObse;
    const guid = v1();
    let responseContent = {};
    (_options$progressObse = options.progressObservable) === null || _options$progressObse === void 0 ? void 0 : _options$progressObse.setValue({
      guid,
      file: options.file,
      completed: false
    });
    const formData = this.getFormDataFromOptions(options);
    formData.append(options.file.name, options.file);
    try {
      const response = await this.repository.fetch(this.getUploadUrl(options), {
        ...options.requestInit,
        credentials: 'include',
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        var _options$progressObse2;
        responseContent = await response.json();
        (_options$progressObse2 = options.progressObservable) === null || _options$progressObse2 === void 0 ? void 0 : _options$progressObse2.setValue({
          guid,
          file: options.file,
          chunkCount: 1,
          uploadedChunks: 1,
          completed: true,
          createdContent: responseContent
        });
      } else {
        throw await this.repository.getErrorFromResponse(response);
      }
      return responseContent;
    } catch (error) {
      var _options$progressObse3;
      (_options$progressObse3 = options.progressObservable) === null || _options$progressObse3 === void 0 ? void 0 : _options$progressObse3.setValue({
        guid,
        file: options.file,
        completed: false,
        error
      });
      throw error;
    }
  }
  async uploadChunked(options) {
    var _options$progressObse4;
    const chunkCount = Math.floor(options.file.size / this.repository.configuration.chunkSize);
    const guid = v1();
    (_options$progressObse4 = options.progressObservable) === null || _options$progressObse4 === void 0 ? void 0 : _options$progressObse4.setValue({
      guid,
      file: options.file,
      completed: false,
      chunkCount,
      uploadedChunks: 0
    });
    const uploadPath = this.getUploadUrl(options);

    /** initial chunk data and request */
    const formData = this.getFormDataFromOptions(options);
    formData.append(options.file.name, options.file.slice(0, this.repository.configuration.chunkSize));
    formData.append('UseChunk', 'true');
    formData.append('create', '1');
    try {
      const initRequest = await this.repository.fetch(uploadPath, {
        ...options.requestInit,
        body: formData,
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Range': `bytes 0-${this.repository.configuration.chunkSize - 1}/${options.file.size}`,
          'Content-Disposition': `attachment; filename="${encodeURIComponent(options.file.name)}"`
        }
      });
      const chunkToken = await initRequest.text();
      let lastResponseContent = {};
      for (let i = 0; i <= chunkCount; i++) {
        const start = i * this.repository.configuration.chunkSize;
        let end = start + this.repository.configuration.chunkSize;
        end = end > options.file.size ? options.file.size : end;
        const chunkFormData = new FormData();
        const chunkData = options.file.slice(start, end);
        chunkFormData.append('FileLength', options.file.size.toString());
        chunkFormData.append('ChunkToken', chunkToken);
        chunkFormData.append('PropertyName', options.binaryPropertyName.toString());
        chunkFormData.append(options.file.name, chunkData);
        const lastResponse = await this.repository.fetch(uploadPath, {
          ...options.requestInit,
          body: chunkFormData,
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Range': `bytes ${start}-${end - 1}/${options.file.size}`,
            'Content-Disposition': `attachment; filename="${encodeURIComponent(options.file.name)}"`
          }
        });
        if (lastResponse.ok) {
          var _options$progressObse5;
          lastResponseContent = await lastResponse.json();
          (_options$progressObse5 = options.progressObservable) === null || _options$progressObse5 === void 0 ? void 0 : _options$progressObse5.setValue({
            guid,
            file: options.file,
            chunkCount,
            uploadedChunks: i,
            completed: i === chunkCount,
            createdContent: lastResponseContent
          });
        } else {
          throw await this.repository.getErrorFromResponse(lastResponse);
        }
      }
      return lastResponseContent;
    } catch (error) {
      var _options$progressObse6;
      (_options$progressObse6 = options.progressObservable) === null || _options$progressObse6 === void 0 ? void 0 : _options$progressObse6.setValue({
        guid,
        file: options.file,
        chunkCount,
        uploadedChunks: 0,
        completed: false,
        error
      });
      throw error;
    }
  }
  async webkitFileHandler(fileEntry, contentPath, options) {
    await new Promise((resolve, reject) => {
      fileEntry.file(async f => {
        await this.file({
          file: f,
          ...options,
          parentPath: contentPath
        });
        resolve();
      }, err => reject(err));
    });
  }
  async webkitDirectoryHandler(directory, contentPath, options, readEntries = true) {
    const folder = await this.repository.post({
      content: {
        Name: directory.name
      },
      parentPath: contentPath,
      contentType: 'Folder'
    });
    if (readEntries) {
      const dirReader = directory.createReader();
      await new Promise((resolve, reject) => {
        dirReader.readEntries(async items => {
          await this.webkitItemListHandler(items, folder.d.Path, true, options);
          resolve();
        }, err => reject(err));
      });
    }
  }
  async webkitItemListHandler(items, contentPath, createFolders, options) {
    for (const item of items) {
      if (createFolders && item.isDirectory) {
        await this.webkitDirectoryHandler(item, contentPath, options);
      }
      if (item.isFile) {
        await this.webkitFileHandler(item, contentPath, options);
      }
    }
  }

  /**
   * Uploads content from a specified Drop Event
   * @param { UploadOptions } options Options for the Upload request
   */
  async fromDropEvent(options) {
    if (window.webkitRequestFileSystem) {
      const entries = options.event.dataTransfer ? [].map.call(options.event.dataTransfer.items, i => i.webkitGetAsEntry()) : [];
      await this.webkitItemListHandler(entries, options.parentPath, options.createFolders, options);
    } else {
      var _options$event$dataTr;
      // Fallback for non-webkit browsers.
      ((_options$event$dataTr = options.event.dataTransfer) === null || _options$event$dataTr === void 0 ? void 0 : _options$event$dataTr.files) && [].forEach.call(options.event.dataTransfer.files, async f => {
        if (f.type === 'file') {
          return await this.file({
            file: f,
            ...options
          });
        }
      });
    }
  }

  /**
   * Uploads files (and optionally creates the directory structure) from a file list
   * @param { UploadFromFileListOptions } options Options for the Upload request
   */
  async fromFileList(options) {
    if (options.createFolders) {
      const directories = new Set(Array.from(options.fileList).map(f => PathHelper.getParentPath(f.webkitRelativePath || f.fullPath || '')));
      const directoriesBySegments = Array.from(directories).map(d => PathHelper.getSegments(d));
      const createdDirectories = new Set();
      for (const directory of directoriesBySegments) {
        let currentPath = options.parentPath;
        for (const segment of directory) {
          const pathToCreate = PathHelper.joinPaths(currentPath, segment);
          if (!createdDirectories.has(pathToCreate)) {
            await this.webkitDirectoryHandler({
              name: segment
            }, currentPath, options, false);
          }
          createdDirectories.add(pathToCreate);
          currentPath = pathToCreate;
        }
      }
      await Promise.all(Array.from(options.fileList).map(async file => {
        await this.file({
          ...options,
          parentPath: PathHelper.joinPaths(options.parentPath, PathHelper.getParentPath(file.webkitRelativePath)),
          file
        });
      }));
    } else {
      const {
        fileList,
        createFolders,
        ...uploadOptions
      } = options;
      for (const file of Array.from(options.fileList)) {
        await this.file({
          ...uploadOptions,
          file
        });
      }
    }
  }
}
//# sourceMappingURL=Upload.js.map